import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    phone: "", city: "",
    address_line1: "", address_line2: "", postcode: "",
    country: "United Kingdom", citizenship: "",
    date_of_birth: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to ABGN-UK, please add your photo");
      navigate("/profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[calc(100vh-5rem)] bg-pearl flex items-center justify-center py-16 px-4">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white border border-navy/10 p-10 rounded-sm" data-testid="register-form">
        <p className="eyebrow">Create your account</p>
        <h1 className="mt-3 font-display text-4xl text-navy">Join ABGN-UK.</h1>
        <p className="mt-3 text-slate-600">A few details so we can issue your membership ID and digital member card. You can upload a photo afterwards.</p>
        <div className="mt-8 space-y-5">
          <p className="eyebrow text-saffron">Identity</p>
          <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="reg-name" />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="reg-email" />
          <Input label="Password (min 6 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="reg-password" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="reg-phone" />
            <Input label="Date of birth (optional)" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testid="reg-dob" />
          </div>

          <div className="pt-4 border-t border-navy/10">
            <p className="eyebrow text-saffron">Address</p>
          </div>
          <Input label="Address line 1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} testid="reg-addr1" />
          <Input label="Address line 2 (optional)" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} testid="reg-addr2" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="City / Town" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="reg-city" />
            <Input label="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} testid="reg-postcode" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="reg-country" />
            <Input label="Citizenship / Nationality" value={form.citizenship} onChange={(v) => setForm({ ...form, citizenship: v })} testid="reg-citizenship" />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="reg-submit"
          className="mt-8 w-full py-3.5 bg-navy text-pearl hover:bg-navy-light rounded-sm font-medium disabled:opacity-60"
        >
          {loading ? "Creating account…" : "Create account"}
        </button>
        <p className="mt-6 text-sm text-slate-600">
          Already a member?{" "}
          <Link to="/login" className="text-navy underline underline-offset-2 hover:text-saffron">Sign in</Link>
        </p>
      </form>
    </section>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
      />
    </label>
  );
}
